import { FC, useCallback, useEffect, useMemo, useState } from "react";
import DataTable from "../../../components/shared/DataTable/DataTable";
import { ServiceQuotation } from "../../../services/service-quotation";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { getDaysLeft } from "../../../util";
import serviceSales, { Subscription } from "../../../services/service-sales";
import { NotificationManager } from "react-notifications";
import appState from "../../../state/AppStateContainer";
import { useHistory, useLocation } from "react-router-dom";
import Selector, { SelectorType } from "../../../components/shared/Selector/Selector";
import SearchBarWithActions from "../../../components/shared/SearchBarWithActions/SearchBarWithActions";
import { Loading } from "../../../components/shared";
import BtButton from "../../bt-button/bt-button";
import PlaceSelector, { placeOptions } from "../../page-supplier-presentation/PlaceSelector";
import { MultiValue } from "react-select";

type OpenRequest = {
  categoryName: string,
  projectName: string,
  orgName: string,
  category: {
    name: string,
    id: string,
    superAdminAreaId?: string,
  },
  org: {
    name: string,
    id: string,
  },
  project: {
    name: string,
    id: string,
  },
  baseURL: string,
  date: string,
  id: string,
  deliveryEnd?: string,
  deliveryStart?: string,
  timeLeft: string,
  version: number,
  region: string,
  place: string,
}

const formatDate = (date: string) => {
  return date && moment(date).locale('sv').format('D MMMM YYYY');
};

const doesPlaceMatch = (place: string, placeToMatch: string) => {
  if (!place || !placeToMatch) return false;
  if (
    (place === 'Hela Sverige' && placeToMatch !== 'Utomlands') ||
    place === placeToMatch ||
    (place === 'Stockholm' && placeToMatch.startsWith('Stockholm'))
  ) return true;
  return false;
}

const fetchRequests = async (onlyPublic?: boolean) => {
  let response;
  if (onlyPublic) {
    response = await ServiceQuotation.fetchAllPublicQuotationsNoSubscription();
  } else {
    response = await ServiceQuotation.fetchAllPublicQuotations();
  }
  const requests = response?.data?.map((request: OpenRequest): OpenRequest => {
    return {
      ...request,
      projectName: request?.project?.name,
      categoryName: request?.category?.name,
      orgName: request?.org?.name
    }
  });

  return requests;
}

type Props = {
  onlyPublic?: boolean,
  openToNewTab?: boolean,
}

export const PublishedTable: FC<Props> = ({ onlyPublic, openToNewTab }) => {
  const [t] = useTranslation('requests');
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const [subscription, setSubscription] = useState<Subscription>();
  const [prioSubscription, setPrioSubscription] = useState<Subscription>();
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState<OpenRequest[]>([]);
  const [searchValue, setSearchValue] = useState('');
  const [places, setPlaces] = useState<string[]>([]);
  const [categories, setCategories] = useState<SelectorType[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<MultiValue<SelectorType>>([]);
  const history = useHistory();

  const hasActiveSubscription = !onlyPublic && (!!subscription?.active?.startDate || !!prioSubscription?.active?.startDate);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        if (!onlyPublic) {
          const subscription = await serviceSales.fetchPublishedQuotationsSubscription();
          setSubscription(subscription);
          const prioSubscription = await serviceSales.fetchRecommendedSubscription();
          setPrioSubscription(prioSubscription);
        }
        
        const requests = await fetchRequests(onlyPublic);
        setRequests(requests);

        const [supplierProfile, areaOptions] = await serviceSales.fetchSupplierProfile();
        setCategories(areaOptions.map(area => ({
          label: area.name,
          value: area._id,
        })));
        // setSelectedCategories(supplierProfile?.areas.map(area => ({label: area.name, value: area._id})));
        // setPlaces(supplierProfile?.places ?? []);
      } catch (e) {
        NotificationManager.error((e as Error).toString(), t('notification.error', 'Kunde inte hämta förfrågningar'));
      }
      setLoading(false);
    })();
  }, []);

  const filteredRequests = useMemo(() => {
    if (!requests) {
      return [];
    }
    return requests.filter(request => {
      const searchValueLower = searchValue.toLowerCase();

      const matchesSearch = searchValueLower === '' ||
        (hasActiveSubscription ? (
          request.projectName.toLowerCase().includes(searchValueLower) ||
          request.orgName.toLowerCase().includes(searchValueLower) ||
          request.categoryName.toLowerCase().includes(searchValueLower)
        ) : (
          request.categoryName.toLowerCase().includes(searchValueLower)
        ));
      
      const placeMatches = places.length > 0 ? places.find(place => doesPlaceMatch(placeOptions.find(p => p.value === place)?.label ?? '', request.place)  ) : true;
      const categoryMatches = selectedCategories.length > 0 ? selectedCategories.find(category => category?.value === request.category.superAdminAreaId) : true;

      return matchesSearch && placeMatches && categoryMatches;
    })
  }, [requests, searchValue, places, selectedCategories]);

  const columns = [
    {
      title: t('table.header.project', 'Projekt'),
      field: "projectName",
      render: (rowData: OpenRequest) => {
        return (
          <span style={{ color: 'var(--gray-900)', fontSize: '16px', fontWeight: 500, }}>
            {rowData.projectName}
          </span>
        );
      },
    },
    {
      title: t('table.header.org', 'Beställare'),
      field: "orgName",
    },
    {
      title: t('table.header.category', 'Yrkeskategori'),
      field: "categoryName",
    },
    {
      title: t('table.header.region', 'Region'),
      field: "place",
    },
    {
      title: t('table.header.version', 'Version'),
      field: "version",
      render: (rowData: OpenRequest) => +rowData.version + 1,
    },
    {
      title: t('table.header.date', 'Skickad'),
      field: "date",
      render: (rowData: OpenRequest) => formatDate(rowData.date),
    },
    {
      title: t('table.header.timeLeft', 'Anbudstid'),
      field: "timeLeft",
      render: (rowData: OpenRequest) => {
        const daysLeft = getDaysLeft(rowData.timeLeft);
        return (
          <span style={{ whiteSpace: 'nowrap' }}>{
            isNaN(+daysLeft) ? daysLeft : t('table.row.timeLeft', '{{count}} dagar', { context: +daysLeft < 0 ? 'expired' : null, count: +daysLeft })
          }</span>
        );
      }
    },
    {
      title: t('table.header.deliveryStart', 'Leveransdatum'),
      field: "deliveryStart",
      render: (rowData: OpenRequest) => {
        const d1 = formatDate(rowData.deliveryStart || '');
        const d2 = formatDate(rowData.deliveryEnd || '');

        if (!d1 && !d2) return '–';

        return [d1, d2].filter(x => x).join(" – ");
      }
    }
  ].filter(column => hasActiveSubscription ? true : ['categoryName', 'place', 'timeLeft'].includes(column.field));
  
  const onRowClick = useCallback((_: any, rowData?: OpenRequest) => {
    if (rowData) {
      const url = `/${appState.getCurrentOrgSlug()}/published-requests/${rowData.org.id}/${rowData.project.id}/${rowData.category.id}`;
      if (openToNewTab) {
        window.open(`${window.location.origin}/hvdLogin?token=${appState.state.token.hvdToken}&product-id=${query.get('product-id')}&urlToRedirect=${encodeURIComponent(url)}`, '_blank');
      } else {
        history.push(url);
      }
    }
  }, []);

  const [currentPage, setCurrentPage] = useState(0);
  const pageSize = 10;
  const isSuperAdmin = appState.isSuperAdmin();
  
  const handlePageChange = useCallback((page: number, pageSize: number) => {
    setCurrentPage(page);
    if (!filteredRequests || filteredRequests.length === 0 || onlyPublic) return;
    if (isSuperAdmin) return;
    
    const startIndex = page * pageSize;
    const endIndex = Math.min(startIndex + pageSize, filteredRequests.length);
    const visibleQuotations = filteredRequests.slice(startIndex, endIndex);
    const quotationIds = visibleQuotations.map(q => q.id).filter(Boolean);
    
    if (quotationIds.length > 0) {
      ServiceQuotation.markQuotationsAsSeen(quotationIds);
    }
  }, [filteredRequests, onlyPublic, isSuperAdmin]);

  const handleMarkAsSeen = useCallback(() => {
    if (!filteredRequests || filteredRequests.length === 0) return;
    
    const startIndex = currentPage * pageSize;
    const endIndex = Math.min(startIndex + pageSize, filteredRequests.length);
    const visibleQuotations = filteredRequests.slice(startIndex, endIndex);
    const quotationIds = visibleQuotations.map(q => q.id).filter(Boolean);
    
    if (quotationIds.length > 0) {
      ServiceQuotation.markQuotationsAsSeen(quotationIds);
      NotificationManager.success(
        t('published.markAsSeen.success', 'Markerade {{count}} förfrågningar som sedda', { count: quotationIds.length }),
        t('published.markAsSeen.title', 'Markerat som sett')
      );
    }
  }, [filteredRequests, currentPage, t]);

  if (loading) {
    return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}><Loading type='inline' /></div>
  }

  return (
    <div>
      <div style={{ marginBottom: '40px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px' }}>
        <SearchBarWithActions
          value={searchValue}
          onChange={setSearchValue}
          style={{}}
          searchicon={undefined}
          className={undefined}
        >
          <div style={{ width: '230px' }}>
            <Selector
              isClearable
              isMulti
              placeholder={t('published.categorySelector', 'Kategori')}
              options={categories}
              value={selectedCategories}
              onChange={selectedCategories => setSelectedCategories(selectedCategories)}
              isOverflow
            />
          </div>
          <div style={{ width: '230px' }}>
            <PlaceSelector
              places={places}
              onChange={setPlaces}
              noTooltip
              placeholderInsteadOfLabel
              isOverflow
            />
          </div>
        </SearchBarWithActions>
        {isSuperAdmin && (
          <BtButton
            color="white"
            onClick={handleMarkAsSeen}
          >
            {t('published.markAsSeen.button', 'Markera som sett')}
          </BtButton>
        )}
      </div>
      <DataTable<OpenRequest> columns={columns} data={filteredRequests} isLoading={loading} onRowClick={hasActiveSubscription ? onRowClick : undefined} pageSize={pageSize} onPageChange={handlePageChange} />
    </div>
  )
}